/**
 * `GET /api/exports/<resource>` — the file downloads (contract #18, task T2.2;
 * the other four resources — T.E1).
 *
 * The api answers with a FILE here, not the JSON envelope, so this module cannot
 * go through `requestEnvelope`: it fetches the bytes itself, hands them to the
 * browser as a download and surfaces a failure as the normal `ApiError` every
 * other module throws. Everything else stays the house rules — the relative
 * `/api` prefix (the ingress owns the routing), the envelope error parsing on a
 * non-2xx, and the full-page redirect on a dead session.
 *
 * The naming/query half of this module is framework-free and lives in
 * `./exports-model`, the way every other `*-model` module does: the `unit`
 * Playwright project imports it in plain Node, where `import.meta.env` of
 * `./client` does not exist.
 *
 * xlsx only: the owner asked for spreadsheets. (#18 also takes `format=csv` for
 * its v1.0 clients; nothing in this app sends it.)
 */

import { API_BASE_URL, ApiError, buildQuery, type QueryValue } from './client';
import { parseErrorBody, statusCodeToCode } from './envelope';
import { exportFallbackName, filenameFromDisposition, type ExportResource } from './exports-model';

export { exportFallbackName, exportParams, filenameFromDisposition, type ExportResource } from './exports-model';

/** The workbook MIME — the one every export answers with. */
export const EXPORT_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Hands `blob` to the browser as a download named `filename`. An anchor click is
 * the only cross-browser way to NAME a programmatic download; the URL is revoked
 * on the next tick, after the browser has taken the reference it needs.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Downloads one export with the current filters. Resolves with the file name the
 * browser was given — the caller may show it; rejects with the documented
 * `ApiError` (`403 FORBIDDEN` below MANAGER, `400 VALIDATION` over the row cap).
 */
export async function downloadExport(
  resource: ExportResource,
  params: Record<string, QueryValue> = {},
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/exports/${resource}${buildQuery(params)}`, {
    headers: { Accept: EXPORT_MIME },
  });

  if (!response.ok) {
    if (response.status === 401) {
      // A dead session behaves like everywhere else: the OAuth entry point, full page.
      window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
    }
    let code = statusCodeToCode(response.status);
    let message = response.statusText || 'неизвестная ошибка';
    try {
      const parsed = parseErrorBody(await response.json(), response.status);
      code = parsed.code;
      message = parsed.message;
    } catch {
      // a file endpoint that fails may answer non-JSON — the status is the message then
    }
    throw new ApiError(response.status, 'GET', `/exports/${resource}`, code, message);
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('Content-Disposition')) ?? exportFallbackName(resource);
  saveBlob(blob, filename);
  return filename;
}
